import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

import type { AcuityHistoryEntry, UUID } from "@/core/types";
import { AcuityScoreBadge } from "@/features/acuity/components/AcuityScoreBadge";
import { AcuityStatusBadge } from "@/features/acuity/components/AcuityStatusBadge";
import dayjs from "@/lib/helpers/dayjs";

import { useAcuityHistory } from "../api/queries/useAcuityHistory";

import { AcuityHistoryTimelineSkeleton } from "./AcuityHistoryTimelineSkeleton";
import { TimelineRecordElement } from "./TimelineRecordElement";

type Params = {
  patientId: UUID;
};

export function AcuityHistoryTimeline() {
  const { patientId } = useParams<Params>();
  const { data: acuityHistory, isLoading } = useAcuityHistory({ patientId });

  if (isLoading) return <AcuityHistoryTimelineSkeleton />;

  return (
    <div className="tw:max-h-[70vh] tw:overflow-y-auto tw:pr-3">
      <ul className="tw:relative tw:ml-1.5 tw:flex tw:flex-col tw:gap-y-5 tw:border-s tw:border-l tw:border-fill-quaternary tw:text-sm tw:text-text-primary">
        {acuityHistory.map((record) => (
          <TimelineRecord key={record.entity_id} {...record} />
        ))}
      </ul>
    </div>
  );
}

function TimelineRecord(props: AcuityHistoryEntry) {
  const {
    additional_details,
    reasoning,
    rejection_reason,
    requestor_name,
    resolved_at,
    score,
    status,
  } = props || {};

  const DETAIL_LIST: Record<string, string>[] = [
    {
      label: "Requested by",
      value: requestor_name,
    },
    {
      label: "Reasoning",
      value: reasoning,
    },
    {
      label: "Additional details",
      value: additional_details,
    },
    {
      label: "Rejection reason",
      value: rejection_reason,
    },
  ];

  const formattedTimestamp = resolved_at
    ? dayjs(resolved_at).format("MM/DD/YYYY h:mm A")
    : undefined;

  return (
    <TimelineRecordElement>
      <div className="tw:flex tw:min-w-40 tw:flex-col tw:gap-y-1 tw:border-r tw:border-fill-quaternary tw:pr-4">
        {formattedTimestamp && <time dateTime={resolved_at}>{formattedTimestamp}</time>}
        <AcuityScoreBadge acuityScore={score} className="tw:m-0 tw:self-baseline" />
      </div>
      <dl className="tw:grid tw:w-full tw:grid-cols-[160px_1fr] tw:gap-y-2">
        <DetailListElement label="Status" value={<AcuityStatusBadge status={status} />} />

        {DETAIL_LIST.filter((item) => Boolean(item.value)).map((item) => (
          <DetailListElement key={item.label} label={item.label} value={item.value} />
        ))}
      </dl>
    </TimelineRecordElement>
  );
}

function DetailListElement({ label, value }: { label: string; value: string | ReactNode }) {
  return (
    <>
      <dt>
        <b>{label}</b>
      </dt>
      <dd className="tw:break-all">{value}</dd>
    </>
  );
}
